import { Flex } from '@televet/kibble-ui/build/chakra';
import { Button } from '@televet/kibble-ui/build/components/Button';
import React, { useCallback, useEffect, useState } from 'react';
import { EmailCampaign, EmailCampaignSegmentationType } from 'shared/types/graphql';
import ConditionalSegments from '../ConditionalSegments/ConditionalSegments';
import { useConditionalSegments } from '../ConditionalSegments/hooks/useConditionalSegments';
import { EmailCampaignSegmentDefinition } from '../ConditionalSegments/types/conditionalSegments.types';
import CustomList from '../CustomList/CustomList';
import useCustomList from '../CustomList/hooks/useCustomList';
import { SegmentOptions } from '../types/recipients.types';
import DefaultSegment from './DefaultSegment';

type SegmentListProps = {
  initiallySelected: SegmentOptions;
  saveRecipientSegmentation: (
    segmentationType: EmailCampaignSegmentationType,
    clinicSegments?: EmailCampaignSegmentDefinition,
  ) => Promise<void>;
  onCancel: () => void;
  recipientCount?: number | null;
  allActiveSegmentationCount?: number | null;
  isSaving?: boolean;
  campaign?: EmailCampaign;
  additionalSegmentProps?: Record<string, unknown>;
  loadingCount?: boolean;
  hasAppointments: boolean;
};

export const SegmentList = ({
  initiallySelected,
  saveRecipientSegmentation,
  isSaving,
  onCancel,
  campaign,
  allActiveSegmentationCount,
  additionalSegmentProps,
  loadingCount,
  hasAppointments,
}: SegmentListProps): JSX.Element => {
  const [selected, setSelected] = useState<SegmentOptions>(initiallySelected);
  const conditionalSegmentData = useConditionalSegments(campaign, additionalSegmentProps);
  const [displayEmptyValidationMessage, setDisplayEmptyValidationMessage] = useState(false);
  const customListData = useCustomList({
    campaign,
    displayEmptyValidationMessage,
    isSelected: selected === SegmentOptions.CustomList,
    onSelect: (): void => {
      setSelected(SegmentOptions.CustomList);
    },
  });
  const { hasUISegments } = conditionalSegmentData;

  const hasCustomListWithValidRecipients = !!campaign?.customList;
  const isLoadingCustomList = customListData.isLoadingCustomList;

  const closeForm = onCancel;

  // Reset validation if conditions are met
  useEffect(() => {
    setDisplayEmptyValidationMessage(false);
  }, [selected]);

  useEffect(() => {
    if (selected === SegmentOptions.CustomList && isLoadingCustomList === false) {
      setDisplayEmptyValidationMessage(false);
    }
  }, [isLoadingCustomList, selected]);

  useEffect(() => {
    if (hasUISegments) {
      // Means it had no segments and the validation was triggered
      // now we want clear the invalid message upon one segment is added
      setDisplayEmptyValidationMessage(false);
    }
  }, [hasUISegments]);

  const removeCustomList = useCallback(async () => {
    if (campaign?.customList) {
      await customListData.deleteCustomList();
    }
  }, [campaign?.customList, customListData]);

  const handleSave = async (): Promise<void> => {
    if (selected === SegmentOptions.AllActive) {
      await removeCustomList();
      await saveRecipientSegmentation(EmailCampaignSegmentationType.AllActiveClients);
      return closeForm();
    }

    if (selected === SegmentOptions.CustomList) {
      if (!hasCustomListWithValidRecipients) {
        setDisplayEmptyValidationMessage(true);
        return;
      }
      await saveRecipientSegmentation(EmailCampaignSegmentationType.CustomList);
      closeForm();
    }

    if (selected === SegmentOptions.Conditional) {
      if (hasUISegments === false) {
        return setDisplayEmptyValidationMessage(true);
      } else if (conditionalSegmentData.validateSegments()) {
        await saveRecipientSegmentation(
          EmailCampaignSegmentationType.ClinicPetParentPrismaWhere,
          conditionalSegmentData.getSegmentsDefinition(),
        );
        await removeCustomList();
        closeForm();
      }
    }
  };

  return (
    <>
      <DefaultSegment
        recipientCount={allActiveSegmentationCount}
        isSelected={selected === SegmentOptions.AllActive}
        loadingCount={loadingCount}
        onSelect={(): void => {
          if (hasAppointments) {
            setSelected(SegmentOptions.AllActive);
          }
        }}
        disabled={!hasAppointments}
      />

      <ConditionalSegments
        displayEmptyValidationMessage={selected === SegmentOptions.Conditional && displayEmptyValidationMessage}
        isSelected={selected === SegmentOptions.Conditional}
        onSelect={(): void => {
          if (hasAppointments) {
            setSelected(SegmentOptions.Conditional);
          }
        }}
        {...conditionalSegmentData}
        disabled={!hasAppointments}
      />

      <CustomList {...customListData} />

      <Flex justifyContent="flex-end" mt="3" mr="3">
        <Button variant="tertiary" mr="4" onClick={onCancel}>
          Cancel
        </Button>
        <Button disabled={isLoadingCustomList || isSaving} isLoading={isSaving} onClick={handleSave}>
          Save
        </Button>
      </Flex>
    </>
  );
};
